import { computed, inject } from '@angular/core';
import { SelfUserStore } from '@domains/person/user/self/data';
import { signalStore, withComputed, withMethods } from '@ngrx/signals';

/**
 * The user's preferences for the mini-monitor (Access Panel). The function keys:
 * F2
 * releases the entry of the focused access point, F3 the exit. Either can be
 * switched off from the panel's gear menu — useful on a station where the
 * operator keeps hitting them by accident, or where F2/F3 already belong to
 * something else at the desk.
 *
 * State lives in the account's `UiClientSettings`, so this store is a read/write
 * facade over `SelfUserStore.uiClientSettings().MiniMonitorShortcuts` and the
 * choice follows the user to any browser once `UpdateUiClientSettingsAsync`
 * (debounced by the shortcuts store) lands. Turning a key off only silences the
 * shortcut — the entry/exit buttons themselves keep working.
 *
 * The same group holds the refusal sound: a beep when a refusal that can still
 * be released arrives, while the mini-monitor is detached. It starts off —
 * many gyms play music at the front desk — and is switched in Settings >
 * Access, "Your preferences", for this user on any computer. Next to it, and
 * just as off by default, the operating-system notification for the same
 * refusals — shown by the tab, and by Web Push even with no tab open
 * (`RefusalWebPushStore` subscribes this browser while it is on). The browser's
 * permission to show it is per browser, not per user, and is not stored here.
 *
 * The stored shape (`IUiClientSettingsMiniMonitorShortcuts`) is not imported
 * here on purpose: `@shared/components/shortcuts/data-access` closes a module
 * cycle back onto this lib, and the booleans type-check structurally on their
 * way into `patchUiClientSettings`.
 */
export const MiniMonitorShortcutsStore = signalStore(
  { providedIn: 'root' },
  withComputed(() => {
    const selfUserStore = inject(SelfUserStore);
    const stored = computed(() => selfUserStore.uiClientSettings()?.MiniMonitorShortcuts);

    /**
     * Only an explicit `false` turns a key off. Anything else — an account that
     * never opened the gear menu, settings written by an older client — reads as
     * enabled, which is how F2/F3 shipped.
     */
    return {
      /** F2 (release entry) answers while true. */
      f2Enabled: computed(() => stored()?.F2Enabled !== false),
      /** F3 (release exit) answers while true. */
      f3Enabled: computed(() => stored()?.F3Enabled !== false),
      /**
       * The opposite rule to F2/F3: only an explicit `true` turns the sound on.
       * An account that never touched it, or settings written by an older
       * client, stay silent.
       */
      refusalSoundEnabled: computed(() => stored()?.RefusalSoundEnabled === true),
      /** Same rule as the sound: only an explicit `true` turns it on. */
      refusalNotificationEnabled: computed(
        () => stored()?.RefusalNotificationEnabled === true
      ),
    };
  }),
  withMethods((store, selfUserStore = inject(SelfUserStore)) => {
    /**
     * Always the whole group, already merged: the group is replaced as a unit,
     * so writing one key alone would switch the others back to their defaults.
     */
    const commit = (
      change: Partial<{
        F2Enabled: boolean;
        F3Enabled: boolean;
        RefusalSoundEnabled: boolean;
        RefusalNotificationEnabled: boolean;
      }>
    ): void => {
      selfUserStore.patchUiClientSettings({
        MiniMonitorShortcuts: {
          F2Enabled: store.f2Enabled(),
          F3Enabled: store.f3Enabled(),
          RefusalSoundEnabled: store.refusalSoundEnabled(),
          RefusalNotificationEnabled: store.refusalNotificationEnabled(),
          ...change,
        },
      });
      selfUserStore.persistUiClientSettings();
    };

    return {
      setF2Enabled(enabled: boolean): void {
        commit({ F2Enabled: enabled });
      },

      setF3Enabled(enabled: boolean): void {
        commit({ F3Enabled: enabled });
      },

      setRefusalSoundEnabled(enabled: boolean): void {
        commit({ RefusalSoundEnabled: enabled });
      },

      setRefusalNotificationEnabled(enabled: boolean): void {
        commit({ RefusalNotificationEnabled: enabled });
      },
    };
  })
);
